<script lang="ts" setup>
import { computed, toRef } from "vue";
import { useProductComponents } from "@/queries/products";
import { useConnectionTemplates } from "@/queries/connection-templates";

interface Props {
  productId: string;
}

const props = defineProps<Props>();

const productIdRef = toRef(() => props.productId ?? "");
const enabled = computed(() => !!productIdRef.value);
const { productComponents, productComponentsLoading, productComponentsError } =
  useProductComponents(productIdRef, enabled);

const services = computed(() => productComponents.value?.services ?? []);
const connections = computed(() => productComponents.value?.connections ?? []);
const datastores = computed(() => productComponents.value?.datastores ?? []);

// connectionTemplateId is an opaque id; map it to the template's display name for the type badge.
const { templates } = useConnectionTemplates();
const templateById = computed(() => new Map(templates.value.map((t) => [t.id, t])));
function templateLabel(templateId: string): string {
  return templateById.value.get(templateId)?.name ?? templateId;
}

const isEmpty = computed(
  () =>
    !!productComponents.value &&
    services.value.length === 0 &&
    connections.value.length === 0 &&
    datastores.value.length === 0,
);
</script>

<template>
  <div class="components-tab">
    <!-- Loading state -->
    <div
      v-if="productComponentsLoading"
      class="d-flex justify-content-center py-5"
    >
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="productComponentsError" class="alert alert-danger">
      {{ productComponentsError.userMessage }}
    </div>

    <!-- Whole response empty -->
    <div v-else-if="isEmpty" class="text-center py-5">
      <i class="bi bi-puzzle display-1 text-muted"></i>
      <h3 class="mt-3 text-muted">No components exposed yet</h3>
      <p class="text-muted mb-1">
        Components are the building blocks subscribers can reuse when customising
        this product. They are authored inside the workflow builder, not here.
      </p>
      <ul class="text-muted small list-unstyled mt-3 mb-0">
        <li>
          <strong>Services</strong> — toggle <em>Is Component</em> in a service's
          properties panel.
        </li>
        <li>
          <strong>Connection slots</strong> — declared on a workflow's service in
          the builder and frozen into the product when you publish.
        </li>
        <li>
          <strong>Datastores</strong> — auto-derived from
          <code>&#123;&#123; datastore.name &#125;&#125;</code> references in your
          workflows.
        </li>
      </ul>
    </div>

    <!-- Components list -->
    <template v-else-if="productComponents">
      <!-- Services -->
      <div class="card mb-4">
        <div class="card-body">
          <h2 class="card-title mb-3">
            Services
            <span class="badge bg-secondary rounded-pill ms-1">{{ services.length }}</span>
          </h2>

          <div v-if="services.length === 0" class="text-muted fst-italic small">
            No services exposed yet
          </div>
          <div v-else>
            <div
              v-for="service in services"
              :key="service.serviceId"
              class="bg-light-subtle p-3 rounded mb-2"
            >
              <div class="d-flex justify-content-between align-items-start gap-2">
                <div class="flex-grow-1">
                  <div class="fw-medium">{{ service.serviceName }}</div>
                  <div class="text-muted small">
                    <i class="bi bi-diagram-3 me-1"></i>{{ service.sourceWorkflowName }}
                  </div>
                </div>
                <span class="badge bg-light text-muted border">
                  {{ service.serviceModelType }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Connection Slots -->
      <div class="card mb-4">
        <div class="card-body">
          <h2 class="card-title mb-3">
            Connection Slots
            <span class="badge bg-secondary rounded-pill ms-1">{{ connections.length }}</span>
          </h2>

          <div v-if="connections.length === 0" class="text-muted fst-italic small">
            No connection slots exposed yet
          </div>
          <div v-else>
            <div
              v-for="connection in connections"
              :key="connection.slotName"
              class="bg-light-subtle p-3 rounded mb-2"
            >
              <div class="d-flex justify-content-between align-items-center gap-2">
                <div class="fw-medium">{{ connection.slotName }}</div>
                <span class="badge bg-light text-muted border">
                  {{ templateLabel(connection.connectionTemplateId) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Datastores -->
      <div class="card mb-4">
        <div class="card-body">
          <h2 class="card-title mb-3">
            Datastores
            <span class="badge bg-secondary rounded-pill ms-1">{{ datastores.length }}</span>
          </h2>

          <div v-if="datastores.length === 0" class="text-muted fst-italic small">
            No datastores exposed yet
          </div>
          <div v-else>
            <div
              v-for="datastore in datastores"
              :key="datastore.name"
              class="bg-light-subtle p-3 rounded mb-2"
            >
              <div class="fw-medium">{{ datastore.name }}</div>
              <div v-if="datastore.description" class="text-muted small">
                {{ datastore.description }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
